import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  LeadAddress,
  LeadDetail,
  LeadStage,
  MarketingCategory,
} from "@shared-core/data/api-types";
import {
  DIRECTLY_SETTABLE_LEAD_STAGES,
  INTEREST_OPTIONS,
  LEAD_STAGE_LABELS,
  buildLeadAddressFromForm,
} from "@shared-core/domain/leads";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { isHttpUrl } from "@shared-core/domain/url";
import { paths } from "@shared-core/routing/routes";
import {
  extractErrorDetailMessage,
  extractFieldErrors,
  extractTharsternReasons,
} from "@shared-core/data/validation";
import {
  formatDateTimeOrDash,
  formatCurrencyGbp,
} from "@shared-core/domain/formatters";
import { LeadStageBadge } from "../app/components/domain/leads/LeadStageBadge";
import { LeadCreateCustomerDialog } from "../app/components/domain/leads/LeadCreateCustomerDialog";
import { LeadLinkExistingCustomerDialog } from "../app/components/domain/leads/LeadLinkExistingCustomerDialog";
import { isFeatureEnabled } from "@shared-core/config/features";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import { Textarea } from "../app/components/primitives/textarea";
import {
  ArrowLeft,
  AlertTriangle,
  Mail,
  Phone,
  Globe,
  MapPin,
  Pencil,
  Paperclip,
  X,
  Briefcase,
} from "lucide-react";
import { ApiError } from "@shared-core/data/api/errors";

function describeMutationError(error: unknown, fallback: string): string {
  const reasons = extractTharsternReasons(error);
  const message = extractErrorDetailMessage(error);
  if (reasons.length > 0) {
    // A 502 CustomerWriteError (Tharstern rejected the write) — the
    // `reasons` array is the part a rep can actually act on; dropping it
    // leaves just the generic envelope message.
    return `${message ?? fallback} — ${reasons.join("; ")}`;
  }
  if (error instanceof ApiError && message === null) {
    return error.detail || fallback;
  }
  return message ?? fallback;
}

export function LeadDetailPage() {
  const { leadId } = useParams<{ leadId: string }>();
  const dataAdapter = useDataAdapter();
  const [lead, setLead] = useState<LeadDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  const [linkCustomerOpen, setLinkCustomerOpen] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [newTicketTitle, setNewTicketTitle] = useState("");
  const [newTicketDueDate, setNewTicketDueDate] = useState("");
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [newContactName, setNewContactName] = useState("");
  const [newContactTitle, setNewContactTitle] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [newContactPhone, setNewContactPhone] = useState("");
  const [newContactReferredBy, setNewContactReferredBy] = useState("");
  const [newContactErrors, setNewContactErrors] = useState<
    Record<string, string>
  >({});
  const [composingDraftForContactId, setComposingDraftForContactId] = useState<
    string | null
  >(null);
  const [draftSubject, setDraftSubject] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  // Per contact: the saved draft's Outlook link (the command result's
  // first link), or null when Outlook gave none.
  const [createdDrafts, setCreatedDrafts] = useState<
    Record<string, string | null>
  >({});
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [addressCity, setAddressCity] = useState("");
  const [addressPostcode, setAddressPostcode] = useState("");
  const [addressCountry, setAddressCountry] = useState("United Kingdom");
  // convert_lead_to_customer's CustomerValidationError carries Tharstern
  // field names (e.g. "Address.AddressLine1"), not this form's field names,
  // so these render as a message list next to the address editor rather
  // than binding per-input like CreateLeadModal's own-field errors do.
  const [convertFieldErrors, setConvertFieldErrors] = useState<string[]>([]);
  // Stage-change confirm step: selecting a stage doesn't fire immediately —
  // it opens a small confirm row with an optional "why?" note (Gary's
  // feedback). A stage change never requires a note.
  const [pendingStage, setPendingStage] = useState<LeadStage | null>(null);
  const [stageChangeNote, setStageChangeNote] = useState("");
  const [newNoteText, setNewNoteText] = useState("");
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [editContactName, setEditContactName] = useState("");
  const [editContactTitle, setEditContactTitle] = useState("");
  const [editContactEmail, setEditContactEmail] = useState("");
  const [editContactPhone, setEditContactPhone] = useState("");
  const [editContactReferredBy, setEditContactReferredBy] = useState("");
  const [pendingAttachmentKind, setPendingAttachmentKind] = useState<
    "quote" | "email" | "other"
  >("quote");
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const attachmentFileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    dataAdapter
      .getLeadById(leadId)
      .then((result) => {
        if (!cancelled) setLead(result);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const status = error instanceof ApiError ? error.status : undefined;
        console.error("sales_leads.detail.load_failed", { status });
        // getLeadById resolves null only on a genuine 404 (see
        // real-adapter.ts); anything that throws here — a 403 from a lead
        // outside the rep's scope, a 500, a network failure — must not be
        // funnelled into the "Lead not found" empty state below, or a
        // permissions/outage error reads as "this record doesn't exist".
        setLoadError(
          status === 403
            ? "You don't have access to this lead."
            : "Couldn't load this lead. Please try again.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, leadId]);

  function handleSelectStage(stage: LeadStage) {
    setPendingStage(stage);
    setStageChangeNote("");
  }

  async function handleConfirmStageChange() {
    if (!leadId || !pendingStage) return;
    const stage = pendingStage;
    setIsMutating(true);
    setMutationError(null);
    try {
      const updated = await dataAdapter.advanceLeadStage(
        leadId,
        stage,
        stageChangeNote.trim() || undefined,
      );
      setLead((prev) => (prev ? { ...prev, ...updated } : prev));
      // The note (if any) is now recorded server-side but advanceLeadStage
      // only returns the narrower LeadListItem — refetch to pick it up in
      // the notes list rather than reconstructing it optimistically.
      if (stageChangeNote.trim()) {
        const detail = await dataAdapter.getLeadById(leadId);
        setLead((prev) =>
          prev && detail ? { ...prev, notes: detail.notes } : prev,
        );
      }
      setPendingStage(null);
      setStageChangeNote("");
    } catch (error) {
      console.error("sales_leads.stage_change.failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setMutationError(
        describeMutationError(error, "Couldn't update the stage. Try again."),
      );
    } finally {
      setIsMutating(false);
    }
  }

  async function handleConvert() {
    if (!leadId) return;
    setIsMutating(true);
    setMutationError(null);
    setConvertFieldErrors([]);
    try {
      const updated = await dataAdapter.convertLeadToCustomer(leadId);
      setLead((prev) =>
        prev ? { ...prev, ...updated, pending_match: null } : prev,
      );
    } catch (error) {
      console.error("sales_leads.convert.failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      const fieldErrors = extractFieldErrors(error);
      if (fieldErrors.length > 0) {
        setConvertFieldErrors(fieldErrors.map((fe) => fe.message));
        setMutationError(
          "Couldn't convert this lead — fix the issues below, most often a missing or incomplete address, and try again.",
        );
        if (
          fieldErrors.some((fe) => fe.field.toLowerCase().startsWith("address"))
        ) {
          setIsEditingAddress(true);
          seedAddressForm(lead?.address ?? null);
        }
      } else {
        setMutationError(
          describeMutationError(
            error,
            "Couldn't convert this lead to a customer. Try again.",
          ),
        );
      }
    } finally {
      setIsMutating(false);
    }
  }

  function seedAddressForm(address: LeadAddress | null) {
    setAddressLine1(address?.address_line1 ?? "");
    setAddressLine2(address?.address_line2 ?? "");
    setAddressCity(address?.city ?? "");
    setAddressPostcode(address?.postcode ?? "");
    setAddressCountry(address?.country ?? "United Kingdom");
  }

  function handleStartEditAddress() {
    seedAddressForm(lead?.address ?? null);
    setIsEditingAddress(true);
  }

  async function handleSaveAddress() {
    if (!leadId) return;
    setIsMutating(true);
    setMutationError(null);
    try {
      const address = buildLeadAddressFromForm({
        addressLine1,
        addressLine2,
        city: addressCity,
        postcode: addressPostcode,
        country: addressCountry,
      });
      const updated = await dataAdapter.updateLead(leadId, { address });
      setLead((prev) => (prev ? { ...prev, ...updated } : prev));
      setIsEditingAddress(false);
      setConvertFieldErrors([]);
    } catch (error) {
      console.error("sales_leads.update_address.failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setMutationError(
        describeMutationError(error, "Couldn't save the address. Try again."),
      );
    } finally {
      setIsMutating(false);
    }
  }

  async function handleResolveMatch(action: "confirm" | "dismiss") {
    if (!leadId) return;
    setIsMutating(true);
    setMutationError(null);
    try {
      const updated = await dataAdapter.resolveLeadConversionMatch(
        leadId,
        action,
      );
      setLead((prev) =>
        prev ? { ...prev, ...updated, pending_match: null } : prev,
      );
    } catch (error) {
      console.error("sales_leads.resolve_match.failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setMutationError(
        describeMutationError(error, "Couldn't resolve the match. Try again."),
      );
    } finally {
      setIsMutating(false);
    }
  }

  async function handleToggleTag(tag: MarketingCategory) {
    if (!leadId || !lead) return;
    const next = lead.interest_tags.includes(tag)
      ? lead.interest_tags.filter((t) => t !== tag)
      : [...lead.interest_tags, tag];
    setIsMutating(true);
    setMutationError(null);
    try {
      const updated = await dataAdapter.updateLead(leadId, {
        interest_tags: next,
      });
      setLead((prev) => (prev ? { ...prev, ...updated } : prev));
    } catch (error) {
      console.error("sales_leads.tag_toggle.failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setMutationError(
        describeMutationError(
          error,
          "Couldn't update interest tags. Try again.",
        ),
      );
    } finally {
      setIsMutating(false);
    }
  }

  async function handleAddContact() {
    if (!leadId || !newContactName.trim()) return;
    setIsMutating(true);
    setMutationError(null);
    setNewContactErrors({});
    try {
      const contacts = await dataAdapter.addLeadContact(leadId, {
        name: newContactName.trim(),
        job_title: newContactTitle.trim() || null,
        email: newContactEmail.trim() || null,
        phone: newContactPhone.trim() || null,
        referred_by: newContactReferredBy.trim() || null,
      });
      setLead((prev) => (prev ? { ...prev, contacts } : prev));
      setNewContactName("");
      setNewContactTitle("");
      setNewContactEmail("");
      setNewContactPhone("");
      setNewContactReferredBy("");
      setIsAddingContact(false);
    } catch (error) {
      console.error("sales_leads.add_contact.failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      const errs = extractFieldErrors(error);
      if (errs.length > 0) {
        const map: Record<string, string> = {};
        for (const fe of errs) {
          if (!(fe.field in map)) map[fe.field] = fe.message;
        }
        setNewContactErrors(map);
        setMutationError("Please fix the highlighted fields and try again.");
      } else {
        setMutationError(
          describeMutationError(error, "Couldn't add the contact. Try again."),
        );
      }
    } finally {
      setIsMutating(false);
    }
  }

  function startEditingContact(contact: {
    id: string;
    name: string;
    job_title?: string | null;
    email?: string | null;
    phone?: string | null;
    referred_by?: string | null;
  }) {
    setEditingContactId(contact.id);
    setEditContactName(contact.name);
    setEditContactTitle(contact.job_title ?? "");
    setEditContactEmail(contact.email ?? "");
    setEditContactPhone(contact.phone ?? "");
    setEditContactReferredBy(contact.referred_by ?? "");
  }

  async function handleSaveContactEdit() {
    if (!leadId || !editingContactId || !editContactName.trim()) return;
    setIsMutating(true);
    setMutationError(null);
    try {
      const contacts = await dataAdapter.updateLeadContact(
        leadId,
        editingContactId,
        {
          name: editContactName.trim(),
          job_title: editContactTitle.trim() || null,
          email: editContactEmail.trim() || null,
          phone: editContactPhone.trim() || null,
          referred_by: editContactReferredBy.trim() || null,
        },
      );
      setLead((prev) => (prev ? { ...prev, contacts } : prev));
      setEditingContactId(null);
    } catch (error) {
      console.error("sales_leads.update_contact.failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setMutationError(
        describeMutationError(error, "Couldn't save the contact. Try again."),
      );
    } finally {
      setIsMutating(false);
    }
  }

  async function handleAddNote() {
    if (!leadId || !newNoteText.trim()) return;
    setIsMutating(true);
    setMutationError(null);
    try {
      const note = await dataAdapter.addLeadNote(leadId, {
        note_text: newNoteText.trim(),
      });
      setLead((prev) =>
        prev ? { ...prev, notes: [note, ...prev.notes] } : prev,
      );
      setNewNoteText("");
    } catch (error) {
      console.error("sales_leads.add_note.failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setMutationError(
        describeMutationError(error, "Couldn't add the note. Try again."),
      );
    } finally {
      setIsMutating(false);
    }
  }

  async function handleAttachFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!leadId || !file) return;
    setIsUploadingAttachment(true);
    setMutationError(null);
    try {
      const attachment = await dataAdapter.addLeadAttachment(
        leadId,
        file,
        pendingAttachmentKind,
      );
      setLead((prev) =>
        prev
          ? { ...prev, attachments: [attachment, ...prev.attachments] }
          : prev,
      );
    } catch (error) {
      console.error("sales_leads.add_attachment.failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setMutationError(
        describeMutationError(error, "Couldn't attach the file. Try again."),
      );
    } finally {
      setIsUploadingAttachment(false);
    }
  }

  async function handleDeleteAttachment(attachmentId: string) {
    if (!leadId) return;
    setIsMutating(true);
    setMutationError(null);
    try {
      await dataAdapter.deleteLeadAttachment(leadId, attachmentId);
      setLead((prev) =>
        prev
          ? {
              ...prev,
              attachments: prev.attachments.filter(
                (a) => a.id !== attachmentId,
              ),
            }
          : prev,
      );
    } catch (error) {
      console.error("sales_leads.delete_attachment.failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setMutationError(
        describeMutationError(
          error,
          "Couldn't remove the attachment. Try again.",
        ),
      );
    } finally {
      setIsMutating(false);
    }
  }

  async function handleCreateEmailDraft(contactId: string) {
    if (!leadId || !draftSubject.trim() || !draftBody.trim()) return;
    setIsCreatingDraft(true);
    setDraftError(null);
    try {
      const result = await dataAdapter.createLeadEmailDraft(leadId, {
        contact_id: contactId,
        subject: draftSubject.trim(),
        body: draftBody.trim(),
      });
      setCreatedDrafts((prev) => ({
        ...prev,
        [contactId]: result.links[0]?.href ?? null,
      }));
      setComposingDraftForContactId(null);
      setDraftSubject("");
      setDraftBody("");
    } catch (error) {
      console.error("sales_leads.create_email_draft.failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setDraftError(
        describeMutationError(
          error,
          "Couldn't create the email draft. Try again.",
        ),
      );
    } finally {
      setIsCreatingDraft(false);
    }
  }

  function handleStartComposeDraft(contactId: string) {
    setComposingDraftForContactId(contactId);
    setDraftSubject("");
    setDraftBody("");
    setDraftError(null);
    setCreatedDrafts((prev) => {
      if (!(contactId in prev)) return prev;
      const next = { ...prev };
      delete next[contactId];
      return next;
    });
  }

  async function handleFileTicket() {
    if (!leadId || !newTicketTitle.trim()) return;
    setIsMutating(true);
    setMutationError(null);
    try {
      const ticket = await dataAdapter.fileLeadTicket(leadId, {
        title: newTicketTitle.trim(),
        due_date: newTicketDueDate || null,
      });
      // Mirrors the backend: get_lead_by_id composes at most one ticket
      // per lead (modules/sales_leads_module/README.md), so the newest
      // filed ticket replaces rather than appends.
      setLead((prev) => (prev ? { ...prev, tickets: [ticket] } : prev));
      setNewTicketTitle("");
      setNewTicketDueDate("");
    } catch (error) {
      console.error("sales_leads.file_ticket.failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setMutationError(
        describeMutationError(error, "Couldn't file the ticket. Try again."),
      );
    } finally {
      setIsMutating(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading lead...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <Link
          to={paths.leads()}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to leads
        </Link>
        <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
          <AlertTriangle className="size-4 shrink-0 mt-0.5" />
          {loadError}
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="space-y-4">
        <Link
          to={paths.leads()}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to leads
        </Link>
        <div className="text-muted-foreground text-sm p-8 text-center border border-border rounded-lg">
          Lead not found
        </div>
      </div>
    );
  }

  const isConverted = lead.stage === "first_order";

  return (
    <div className="space-y-6">
      <Link
        to={paths.leads()}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to leads
      </Link>

      {mutationError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
          <AlertTriangle className="size-4 shrink-0 mt-0.5" />
          <div>
            <div>{mutationError}</div>
            {convertFieldErrors.length > 0 && (
              <ul className="list-disc list-inside mt-1">
                {convertFieldErrors.map((message, i) => (
                  <li key={i}>{message}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl mb-1">{lead.company_name}</h1>
        </div>
        <div className="flex items-center gap-3">
          <LeadStageBadge stage={lead.stage} />
          {!isConverted && (
            <select
              aria-label="Lead stage"
              value={lead.stage}
              onChange={(e) => handleSelectStage(e.target.value as LeadStage)}
              disabled={isMutating}
              className="px-3 py-1.5 border border-border rounded bg-background text-sm"
            >
              {DIRECTLY_SETTABLE_LEAD_STAGES.map((stage) => (
                <option key={stage} value={stage}>
                  {LEAD_STAGE_LABELS[stage]}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {pendingStage && (
        <div className="rounded-lg border border-border bg-surface p-3 space-y-2">
          <div className="text-sm text-foreground">
            Move to <strong>{LEAD_STAGE_LABELS[pendingStage]}</strong>? You can
            add an optional note explaining why.
          </div>
          <Textarea
            value={stageChangeNote}
            onChange={(e) => setStageChangeNote(e.target.value)}
            placeholder="Note (optional)"
            aria-label="Stage change note"
            className="text-sm min-h-16"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={isMutating}
              onClick={() => void handleConfirmStageChange()}
            >
              Confirm
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={isMutating}
              onClick={() => {
                setPendingStage(null);
                setStageChangeNote("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {lead.pending_match && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-4 space-y-3">
          <div className="text-sm text-amber-900 dark:text-amber-200">
            A new Tharstern customer,{" "}
            <strong>
              {lead.pending_match.matched_tharstern_customer_name}
            </strong>{" "}
            (code {lead.pending_match.matched_tharstern_customer_code}), looks
            like it might be this lead — matched on{" "}
            {lead.pending_match.matched_signals.join(", ")}, but not a confident
            email match, so it needs a quick check rather than auto-converting.
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={isMutating}
              onClick={() => void handleResolveMatch("confirm")}
            >
              Yes, this is the same company
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={isMutating}
              onClick={() => void handleResolveMatch("dismiss")}
            >
              No, different company
            </Button>
          </div>
        </div>
      )}

      {!isConverted && !lead.pending_match && (
        <div className="flex justify-end">
          {/* With v1_completion on, a won lead's customer is created in CIS
              from the "New customer" form. Off, today's Tharstern path; it
              goes with the flag (v1 completion C5, ruling 1). */}
          {isFeatureEnabled("v1_completion") ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={isMutating}
                onClick={() => setCreateCustomerOpen(true)}
              >
                Won — create customer
              </Button>
              <Button
                variant="outline"
                disabled={isMutating}
                onClick={() => setLinkCustomerOpen(true)}
              >
                Won — existing customer…
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              disabled={isMutating}
              onClick={() => void handleConvert()}
            >
              Won — create Tharstern customer
            </Button>
          )}
        </div>
      )}
      {linkCustomerOpen && (
        <LeadLinkExistingCustomerDialog
          lead={lead}
          open={linkCustomerOpen}
          onOpenChange={setLinkCustomerOpen}
          onConverted={(updated) => {
            setLinkCustomerOpen(false);
            setLead((prev) =>
              prev ? { ...prev, ...updated, pending_match: null } : prev,
            );
          }}
        />
      )}
      {createCustomerOpen && (
        <LeadCreateCustomerDialog
          lead={lead}
          open={createCustomerOpen}
          onOpenChange={setCreateCustomerOpen}
          onConverted={(updated) => {
            setCreateCustomerOpen(false);
            setLead((prev) =>
              prev ? { ...prev, ...updated, pending_match: null } : prev,
            );
          }}
        />
      )}

      {isConverted && (
        <div className="rounded-lg border border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-800 p-4 text-sm text-green-900 dark:text-green-200">
          This lead has converted to a customer.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="border border-border rounded-lg p-4 space-y-3">
            <h2 className="text-sm font-medium text-foreground">
              Company details
            </h2>
            <div className="space-y-2 text-sm">
              {!isEditingAddress && (
                <div className="flex items-start gap-2 text-muted-foreground">
                  <MapPin className="size-4 shrink-0 mt-0.5" />
                  {lead.address ? (
                    <span>
                      {[
                        lead.address.address_line1,
                        lead.address.address_line2,
                        lead.address.city,
                        lead.address.postcode,
                        lead.address.country,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  ) : (
                    <span>No address on file.</span>
                  )}
                  {!isConverted && (
                    <button
                      type="button"
                      className="text-primary hover:underline text-xs ml-1"
                      onClick={handleStartEditAddress}
                    >
                      {lead.address ? "Edit" : "Add address"}
                    </button>
                  )}
                </div>
              )}

              {isEditingAddress && (
                <div className="space-y-2 border border-border rounded-md p-3">
                  <Input
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    placeholder="Address line 1"
                    aria-label="Address line 1"
                    className="text-sm"
                  />
                  <Input
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                    placeholder="Address line 2 (optional)"
                    aria-label="Address line 2"
                    className="text-sm"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      value={addressCity}
                      onChange={(e) => setAddressCity(e.target.value)}
                      placeholder="City"
                      aria-label="City"
                      className="text-sm"
                    />
                    <Input
                      value={addressPostcode}
                      onChange={(e) => setAddressPostcode(e.target.value)}
                      placeholder="Postcode"
                      aria-label="Postcode"
                      className="text-sm"
                    />
                    <Input
                      value={addressCountry}
                      onChange={(e) => setAddressCountry(e.target.value)}
                      placeholder="Country"
                      aria-label="Country"
                      className="text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={isMutating}
                      onClick={() => void handleSaveAddress()}
                    >
                      Save address
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isMutating}
                      onClick={() => setIsEditingAddress(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              {lead.phone && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="size-4 shrink-0" />
                  {lead.phone}
                </div>
              )}
              {lead.website && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Globe className="size-4 shrink-0" />
                  {isHttpUrl(lead.website) ? (
                    <a
                      href={lead.website}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline"
                    >
                      {lead.website}
                    </a>
                  ) : (
                    <span>{lead.website}</span>
                  )}
                </div>
              )}
            </div>
            <div className="pt-1">
              <div className="text-xs text-muted-foreground mb-1.5">
                Interested in
              </div>
              <div className="flex flex-wrap gap-2">
                {INTEREST_OPTIONS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    disabled={isMutating || isConverted}
                    onClick={() => void handleToggleTag(tag)}
                    aria-pressed={lead.interest_tags.includes(tag)}
                    className={`px-3 py-1 rounded-full text-xs capitalize transition-colors border ${
                      lead.interest_tags.includes(tag)
                        ? "bg-primary text-white border-primary"
                        : "bg-surface border-border text-foreground hover:bg-surface-hover"
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            <div className="pt-3 border-t border-border space-y-2">
              <div className="text-xs text-muted-foreground">Company notes</div>
              {lead.notes.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  No notes yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {lead.notes.map((note) => (
                    <div
                      key={note.id}
                      className="text-sm border border-border rounded-md p-2"
                    >
                      {note.stage_change_from && note.stage_change_to && (
                        <Badge variant="secondary" className="mb-1 text-[10px]">
                          {LEAD_STAGE_LABELS[note.stage_change_from]} →{" "}
                          {LEAD_STAGE_LABELS[note.stage_change_to]}
                        </Badge>
                      )}
                      <div className="text-foreground">{note.note_text}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {formatDateTimeOrDash(note.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <Textarea
                  placeholder="Add a note..."
                  aria-label="New company note"
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  className="text-sm min-h-10"
                />
                <Button
                  size="sm"
                  disabled={isMutating || !newNoteText.trim()}
                  onClick={() => void handleAddNote()}
                >
                  Add
                </Button>
              </div>
            </div>
          </section>

          <section className="border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-foreground">Contacts</h2>
              {!isAddingContact && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsAddingContact(true)}
                >
                  Add contact
                </Button>
              )}
            </div>
            {lead.contacts.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No contacts yet.
              </div>
            ) : (
              <div className="space-y-3">
                {lead.contacts.map((contact) =>
                  editingContactId === contact.id ? (
                    <div
                      key={contact.id}
                      className="pb-3 border-b border-border last:border-0 last:pb-0 space-y-2"
                    >
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          value={editContactName}
                          onChange={(e) => setEditContactName(e.target.value)}
                          placeholder="Name"
                          aria-label="Edit contact name"
                          className="text-sm"
                        />
                        <Input
                          value={editContactTitle}
                          onChange={(e) => setEditContactTitle(e.target.value)}
                          placeholder="Job title"
                          aria-label="Edit job title"
                          className="text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          value={editContactEmail}
                          onChange={(e) => setEditContactEmail(e.target.value)}
                          placeholder="Email"
                          aria-label="Edit email"
                          type="email"
                          className="text-sm"
                        />
                        <Input
                          value={editContactPhone}
                          onChange={(e) => setEditContactPhone(e.target.value)}
                          placeholder="Phone"
                          aria-label="Edit phone"
                          className="text-sm"
                        />
                      </div>
                      <Input
                        value={editContactReferredBy}
                        onChange={(e) =>
                          setEditContactReferredBy(e.target.value)
                        }
                        placeholder="Referred by (optional)"
                        aria-label="Edit referred by"
                        className="text-sm"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={isMutating || !editContactName.trim()}
                          onClick={() => void handleSaveContactEdit()}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isMutating}
                          onClick={() => setEditingContactId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={contact.id}
                      className="pb-3 border-b border-border last:border-0 last:pb-0 space-y-2"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="font-medium text-foreground flex items-center gap-1.5">
                            {contact.name}
                            <button
                              type="button"
                              aria-label={`Edit ${contact.name}`}
                              onClick={() => startEditingContact(contact)}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Pencil className="size-3.5" />
                            </button>
                          </div>
                          {contact.job_title && (
                            <div className="text-sm text-muted-foreground">
                              {contact.job_title}
                            </div>
                          )}
                          {contact.referred_by && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Referred by: {contact.referred_by}
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground text-right space-y-1 shrink-0">
                          {contact.email && (
                            <div className="flex items-center gap-1.5 justify-end">
                              <Mail className="size-3.5" />
                              <a
                                href={`mailto:${contact.email}`}
                                className="hover:text-foreground"
                              >
                                {contact.email}
                              </a>
                            </div>
                          )}
                          {contact.phone && (
                            <div className="flex items-center gap-1.5 justify-end">
                              <Phone className="size-3.5" />
                              {contact.phone}
                            </div>
                          )}
                          {contact.email &&
                            composingDraftForContactId !== contact.id && (
                              <button
                                type="button"
                                className="text-primary hover:underline text-xs"
                                onClick={() =>
                                  handleStartComposeDraft(contact.id)
                                }
                              >
                                Draft email
                              </button>
                            )}
                        </div>
                      </div>

                      {contact.id in createdDrafts && (
                        <div className="text-xs text-muted-foreground bg-surface border border-border rounded-md p-2">
                          Draft created.{" "}
                          {createdDrafts[contact.id] ? (
                            <a
                              href={createdDrafts[contact.id] ?? undefined}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary hover:underline"
                            >
                              Open in Outlook
                            </a>
                          ) : (
                            "Find it in your Outlook drafts."
                          )}
                        </div>
                      )}

                      {composingDraftForContactId === contact.id && (
                        <div className="space-y-2 border border-border rounded-md p-3">
                          {draftError && (
                            <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-2 text-xs text-red-700 dark:text-red-300">
                              <AlertTriangle className="size-3.5 shrink-0 mt-0.5" />
                              {draftError}
                            </div>
                          )}
                          <Input
                            value={draftSubject}
                            onChange={(e) => setDraftSubject(e.target.value)}
                            placeholder="Subject"
                            aria-label="Email subject"
                            className="text-sm"
                          />
                          <textarea
                            value={draftBody}
                            onChange={(e) => setDraftBody(e.target.value)}
                            placeholder="Body"
                            aria-label="Email body"
                            rows={4}
                            className="w-full text-sm border border-border rounded bg-background p-2"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              disabled={
                                isCreatingDraft ||
                                !draftSubject.trim() ||
                                !draftBody.trim()
                              }
                              onClick={() =>
                                void handleCreateEmailDraft(contact.id)
                              }
                            >
                              {isCreatingDraft ? "Creating…" : "Create draft"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isCreatingDraft}
                              onClick={() =>
                                setComposingDraftForContactId(null)
                              }
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ),
                )}
              </div>
            )}

            {isAddingContact && (
              <div className="pt-3 border-t border-border space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Input
                      placeholder="Name"
                      aria-label="Contact name"
                      value={newContactName}
                      onChange={(e) => setNewContactName(e.target.value)}
                      className="text-sm"
                      aria-invalid={newContactErrors.name ? true : undefined}
                      aria-describedby={
                        newContactErrors.name
                          ? "new-contact-name-error"
                          : undefined
                      }
                    />
                    {newContactErrors.name && (
                      <p
                        id="new-contact-name-error"
                        className="text-xs text-red-600 dark:text-red-400"
                      >
                        {newContactErrors.name}
                      </p>
                    )}
                  </div>
                  <Input
                    placeholder="Job title"
                    aria-label="Job title"
                    value={newContactTitle}
                    onChange={(e) => setNewContactTitle(e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Input
                      placeholder="Email"
                      aria-label="Email"
                      type="email"
                      value={newContactEmail}
                      onChange={(e) => setNewContactEmail(e.target.value)}
                      className="text-sm"
                      aria-invalid={newContactErrors.email ? true : undefined}
                      aria-describedby={
                        newContactErrors.email
                          ? "new-contact-email-error"
                          : undefined
                      }
                    />
                    {newContactErrors.email && (
                      <p
                        id="new-contact-email-error"
                        className="text-xs text-red-600 dark:text-red-400"
                      >
                        {newContactErrors.email}
                      </p>
                    )}
                  </div>
                  <Input
                    placeholder="Phone"
                    aria-label="Phone"
                    value={newContactPhone}
                    onChange={(e) => setNewContactPhone(e.target.value)}
                    className="text-sm"
                  />
                </div>
                <Input
                  placeholder="Referred by (optional)"
                  aria-label="Referred by"
                  value={newContactReferredBy}
                  onChange={(e) => setNewContactReferredBy(e.target.value)}
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={isMutating || !newContactName.trim()}
                    onClick={() => void handleAddContact()}
                  >
                    Save contact
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isMutating}
                    onClick={() => {
                      setIsAddingContact(false);
                      setNewContactErrors({});
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>

        <div className="space-y-6">
          {isConverted && lead.last_job && (
            <section className="border border-border rounded-lg p-4 space-y-2">
              <h2 className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <Briefcase className="size-4" />
                Client snapshot
              </h2>
              <div className="text-sm text-muted-foreground space-y-1">
                <div>
                  Last job{" "}
                  <span className="text-foreground font-medium">
                    #{lead.last_job.job_number}
                  </span>
                </div>
                <div>{formatDateOrDash(lead.last_job.job_date)}</div>
                {lead.last_job.value_amt != null && (
                  <div className="text-foreground font-medium">
                    {formatCurrencyGbp(lead.last_job.value_amt)}
                  </div>
                )}
              </div>
            </section>
          )}

          <section className="border border-border rounded-lg p-4 space-y-3">
            <h2 className="text-sm font-medium text-foreground">
              Follow-up tickets
            </h2>
            <p className="text-xs text-muted-foreground">
              Every action-needed follow-up is a ticket, filed under the CRM
              category — same priority ordering as everywhere else. Only the
              most recent follow-up shows here today.
            </p>
            {lead.tickets.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No open tickets.
              </div>
            ) : (
              <div className="space-y-2">
                {lead.tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="text-sm border border-border rounded-md p-2"
                  >
                    <div className="font-medium text-foreground">
                      {ticket.title}
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                      <span>Due {formatDateOrDash(ticket.due_date)}</span>
                      <Badge
                        variant={
                          ticket.priority === "urgent" ||
                          ticket.priority === "high"
                            ? "error"
                            : "secondary"
                        }
                        className="text-[10px]"
                      >
                        {ticket.priority}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="pt-2 border-t border-border space-y-2">
              <Input
                placeholder="New follow-up..."
                value={newTicketTitle}
                onChange={(e) => setNewTicketTitle(e.target.value)}
                className="text-sm"
              />
              <Input
                type="date"
                value={newTicketDueDate}
                onChange={(e) => setNewTicketDueDate(e.target.value)}
                className="text-sm"
              />
              <Button
                size="sm"
                className="w-full"
                disabled={isMutating || !newTicketTitle.trim()}
                onClick={() => void handleFileTicket()}
              >
                Add follow-up ticket
              </Button>
            </div>
          </section>

          <section className="border border-border rounded-lg p-4 space-y-3">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-1.5">
              <Paperclip className="size-4" />
              Attachments
            </h2>
            {lead.attachments.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No quotes or emails attached yet.
              </div>
            ) : (
              <div className="space-y-1.5">
                {lead.attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center justify-between gap-2 text-sm border border-border rounded-md px-2 py-1.5"
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{attachment.filename}</span>
                      <Badge
                        variant="secondary"
                        className="text-[10px] shrink-0"
                      >
                        {attachment.kind}
                      </Badge>
                    </div>
                    <button
                      type="button"
                      aria-label={`Remove ${attachment.filename}`}
                      disabled={isMutating}
                      onClick={() => void handleDeleteAttachment(attachment.id)}
                      className="text-muted-foreground hover:text-foreground shrink-0"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <select
                aria-label="Attachment kind"
                value={pendingAttachmentKind}
                onChange={(e) =>
                  setPendingAttachmentKind(
                    e.target.value as "quote" | "email" | "other",
                  )
                }
                className="px-2 py-1.5 border border-border rounded bg-background text-sm"
              >
                <option value="quote">Quote</option>
                <option value="email">Email</option>
                <option value="other">Other</option>
              </select>
              <Button
                size="sm"
                variant="outline"
                disabled={isUploadingAttachment}
                onClick={() => attachmentFileInputRef.current?.click()}
              >
                {isUploadingAttachment ? "Uploading…" : "Attach file"}
              </Button>
              <input
                ref={attachmentFileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => void handleAttachFile(e)}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
